import { IUpdateCartItemRequestV2 } from '@/api/types';
import { getRouterPath } from '@/components';
import { useIsLoggedIn } from '@/entities';
import { useAddCartItemsMutation } from '@/entities/cart/queries';
import { useUpsertOrderEntry } from '@/entities/payment';
import { useLoginInducingModal } from '@/hooks/useLoginInducingModal';
import { trackEvent } from '@vuddy/utils';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { AddToCartToastContent } from '../common/AddToCartToastContent';
import { useProductItemList } from '../common/useProductItemList';
import { useVerificationCode } from '../common/useVerificationCode';
import * as toastStyles from './cartToast.css';
import { isSameCartItem } from './ProductSelectDropdown';
import { SelectedProductOption } from './types';

interface UsePhysicalPurchaseLogicParams {
  salePageId: number;
  cartItems: SelectedProductOption[];
  setCartItems: React.Dispatch<React.SetStateAction<SelectedProductOption[]>>;
  onClose?: VoidFunction;
}

export const usePhysicalPurchaseLogic = ({
  salePageId,
  cartItems,
  setCartItems,
  onClose,
}: UsePhysicalPurchaseLogicParams) => {
  const routerPath = getRouterPath();
  const router = useRouter();
  const { isLoggedIn } = useIsLoggedIn();
  const loginConfirm = useLoginInducingModal();
  const { data, itemList } = useProductItemList(salePageId);
  const { mutateAsync: upsertOrderEntry, isPending: isUpsertingOrderEntry } =
    useUpsertOrderEntry();

  const { channelId, type } = data || {};
  const isFunding = type === 'FUNDING';
  const isClass = type === 'CLASS';

  const verification = useVerificationCode(toastStyles.verificationCodeToast);

  // 부모(BottomSheet/Summary)의 cartItems를 로컬 복사본으로 관리.
  // 드롭다운 선택·수량 변경 시 로컬 먼저 반영 → 부모에 동기화하여
  // 불필요한 리렌더 사이클을 방지한다.
  const [localCartItems, setLocalCartItems] =
    useState<SelectedProductOption[]>(cartItems);

  useEffect(() => {
    setLocalCartItems(cartItems);
  }, [cartItems]);

  const localCartItemsRef = useRef(localCartItems);
  localCartItemsRef.current = localCartItems;

  const verificationRef = useRef(verification);
  verificationRef.current = verification;

  const handleQuantityChange = useCallback(
    (cartItem: SelectedProductOption, newQuantity: number) => {
      setLocalCartItems(prev => {
        const newItems = prev.map((item: SelectedProductOption) =>
          isSameCartItem(item, cartItem)
            ? { ...item, quantity: newQuantity }
            : item
        );
        setCartItems(newItems);
        return newItems;
      });
    },
    [setCartItems]
  );

  const totalSelectedAmount = useMemo(() => {
    return localCartItems.reduce((sum, cartItem) => {
      const quantity = Number.isFinite(cartItem.quantity)
        ? Math.max(1, cartItem.quantity)
        : 1;
      return sum + (cartItem.totalPrice ?? 0) * quantity;
    }, 0);
  }, [localCartItems]);

  // 장바구니 담기 버튼 노출 조건:
  // - PHYSICAL 타입 + NORMAL/RESERVATION 판매 방식만 허용
  // - PLATFORM 배송: 실물·교환권 모두 장바구니 가능
  // - CREATOR 배송: 실물 상품만 장바구니 가능 (교환권 포함 시 불가)
  // - FUNDING/CLASS 타입은 장바구니 미지원
  const isAddToCartAllowed = useMemo(() => {
    const sp = data;
    if (!sp) return false;
    if (isFunding || isClass) return false;
    if (sp.type !== 'PHYSICAL') return false;

    const method = sp.physical?.method;
    if (!method || (method !== 'NORMAL' && method !== 'RESERVATION')) {
      return false;
    }

    const deliveryType = sp.physical?.deliveryType;

    if (deliveryType === 'PLATFORM') {
      return true;
    }
    if (deliveryType === 'CREATOR') {
      const hasVoucher = itemList?.some(
        p => (p.exchangeTickets?.length ?? 0) > 0
      );
      return !hasVoucher;
    }
    return false;
  }, [data, itemList, isFunding, isClass]);

  // "바로 구매" CTA 클릭 → 주문서 페이지로 이동.
  // promotionCode가 필요한 상품이 미인증 상태이면 토스트로 차단.
  // localCartItems·verification은 ref로 참조하여 불필요한 콜백 재생성을 방지한다.
  const onClickCTA = useCallback(async () => {
    if (!isLoggedIn) {
      loginConfirm();
      return;
    }

    if (isUpsertingOrderEntry) {
      return;
    }

    const currentItems = localCartItemsRef.current;
    const { verifiedProductCodes } = verificationRef.current;

    const hasUnverifiedItem = currentItems.some(item => {
      const product = itemList?.find(p => p.id === item.productId);
      return (
        product?.requiresPromotionCode &&
        !verifiedProductCodes.has(item.productId)
      );
    });
    if (hasUnverifiedItem) {
      toast.info('코드를 입력해주세요.', {
        className: toastStyles.verificationCodeToast,
      });
      return;
    }

    const orderItems = currentItems.map(item => {
      const hasOptions = !!(
        item.optionCategories && item.optionCategories.length > 0
      );

      // 스펙: 상품 수량은 선택 수량, 옵션 수량도 선택 수량을 그대로 반영
      // 옵션이 없는 경우에도 동일하게 상품 수량에 선택 수량 반영
      const baseItem = {
        referenceId: item.productId,
        referenceType: isFunding ? ('FUNDING' as const) : ('PHYSICAL' as const),
        quantity: item.quantity,
        promotionCode: verifiedProductCodes.get(item.productId),
      };

      if (hasOptions) {
        const options = item.optionCategories!.flatMap(category =>
          category.optionMenuIds.map(optionId => ({
            referenceId: optionId,
            quantity: item.quantity,
          }))
        );

        if (options.length > 0) {
          return {
            ...baseItem,
            options,
          };
        }
      }

      return baseItem;
    });

    trackEvent('shop', 'click_sale_bottom_buy', {
      channel_id: data?.channelId,
      sale_type: data?.type,
      product_id: orderItems.map(({ referenceId }) => referenceId),
    });

    if (!channelId) {
      toast.error('채널이 없습니다.');
      return;
    }

    try {
      const { entryId } = await upsertOrderEntry({
        salePageId,
        entrySource: 'bottomSheet',
        channelId,
        orderItems,
      });

      router.push(routerPath.orderSheetByEntry(salePageId, entryId));
    } catch {}
  }, [
    channelId,
    data,
    isFunding,
    isLoggedIn,
    isUpsertingOrderEntry,
    itemList,
    loginConfirm,
    router,
    routerPath,
    salePageId,
    upsertOrderEntry,
  ]);

  const addCartItems = useAddCartItemsMutation();

  // 장바구니 담기: 로컬 카트 아이템을 서버 장바구니 API로 전송.
  // optionMenuIds에서 -1(미선택 플레이스홀더)을 제거하고, 빈 옵션 카테고리를 필터링.
  const onClickAddToCart = useCallback(async () => {
    if (!isLoggedIn) {
      loginConfirm();
      return;
    }

    trackEvent('cart', 'click_add_to_cart_bottomsheet', {
      channel_name: data?.channel?.name,
      channel_id: data?.channelId,
      sale_page_id: salePageId,
      sale_type: data?.type,
    });

    const currentItems = localCartItemsRef.current;

    try {
      const payload: IUpdateCartItemRequestV2[] = currentItems.map(item => ({
        productId: item.productId,
        options: item.optionCategories
          ?.map(optionCategory => ({
            id: optionCategory.optionCategoryId,
            optionIds: optionCategory.optionMenuIds.filter(id => id !== -1), // -1 값 제거
          }))
          .filter(optionCategory => optionCategory.optionIds.length > 0), // 빈 옵션 카테고리 제거
        quantity: item.quantity ?? 1,
        isAdd: true,
      }));

      await addCartItems.mutateAsync(payload);

      toast.info(<AddToCartToastContent />, {
        toastId: 'add-to-cart-success',
        onClick: () => {
          toast.dismiss('add-to-cart-success');
          router.push('/cart');
        },
        className: toastStyles.addToCartToast,
      });
    } catch {
      return;
    }

    onClose?.();
  }, [
    addCartItems,
    data,
    isLoggedIn,
    loginConfirm,
    onClose,
    router,
    salePageId,
  ]);

  const addCartItem = useCallback(
    (
      items:
        | SelectedProductOption[]
        | ((prev: SelectedProductOption[]) => SelectedProductOption[])
    ) => {
      setLocalCartItems(prev => {
        const newItems = typeof items === 'function' ? items(prev) : items;

        // 새로 추가된 아이템들 찾기
        const addedItems = newItems.filter(
          item => !prev.some(existing => isSameCartItem(existing, item))
        );

        // 기존 아이템의 순서를 유지하면서 새로운 아이템을 맨 앞에 추가
        const existingItems = prev.filter(item =>
          newItems.some(newItem => isSameCartItem(item, newItem))
        );
        const finalItems = [...addedItems, ...existingItems];
        setCartItems(finalItems);
        return finalItems;
      });
    },
    [setCartItems]
  );

  const deleteCartItem = useCallback(
    (cartItem: SelectedProductOption) => {
      setLocalCartItems(prev => {
        const newItems = prev.filter(item => !isSameCartItem(item, cartItem));
        setCartItems(newItems);
        return newItems;
      });
    },
    [setCartItems]
  );

  return {
    data,
    itemList,
    isClass,
    localCartItems,
    totalSelectedAmount,
    isAddToCartAllowed,
    verification,
    handleQuantityChange,
    onClickCTA,
    onClickAddToCart,
    addCartItem,
    deleteCartItem,
  };
};
